import {
  DataFilterDefinitionsDtoResponse,
  NavItemLabelDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import { QueryClient } from '@tanstack/react-query';

import {
  parseComparisonDiscriminatorValues,
  stringifyComparisonEntityPool,
} from './comparisonDetailPageEntities';
import { COMPARISON_ENTITIES_URL_PARAM } from './constants';
import { getDetailsFromSearchParams } from './getDetailsFromSearchParams';

export function comparisonEntityPoolsMatch(
  storedSearchParams: URLSearchParams,
  currentSearchParams: URLSearchParams
): boolean {
  const storedPool = stringifyComparisonEntityPool(storedSearchParams);
  const currentPool = stringifyComparisonEntityPool(currentSearchParams);
  return storedPool.length > 0 && storedPool === currentPool;
}

/** Parent/child detail sub-pages share one comparison nav session; slug siblings do not. */
export function comparisonNavPathnamesMatch(
  storedPathname: string,
  currentPathname: string
): boolean {
  return (
    storedPathname === currentPathname ||
    currentPathname.startsWith(`${storedPathname}/`) ||
    storedPathname.startsWith(`${currentPathname}/`)
  );
}

export function matchesComparisonNavSession(
  storedHref: string,
  currentPathname: string,
  currentSearchParams: URLSearchParams
): boolean {
  if (!currentSearchParams.has(COMPARISON_ENTITIES_URL_PARAM)) {
    return false;
  }

  const storedUrl = new URL(storedHref, 'http://localhost');

  return (
    comparisonNavPathnamesMatch(storedUrl.pathname, currentPathname) &&
    comparisonEntityPoolsMatch(
      new URLSearchParams(storedUrl.search),
      currentSearchParams
    )
  );
}

function getFilterOptionValue(
  entry:
    | string
    | number
    | { value?: string | boolean | null; label?: string | null }
): string | undefined {
  if (typeof entry === 'string' || typeof entry === 'number') {
    return String(entry);
  }

  if (entry.value == null) {
    return undefined;
  }

  return String(entry.value);
}

function getFilterOptionLabel(
  entry:
    | string
    | number
    | { value?: string | boolean | null; label?: string | null }
): string | undefined {
  if (typeof entry === 'string' || typeof entry === 'number') {
    return undefined;
  }

  return entry.label != null && entry.label !== ''
    ? String(entry.label)
    : undefined;
}

export function findOptionLabel(
  filterData: DataFilterDefinitionsDtoResponse['data'][string] | undefined,
  value: string
): string | undefined {
  if (filterData == null || filterData.type === 'error') {
    return undefined;
  }

  if (!('data' in filterData) || filterData.data == null) {
    return undefined;
  }

  const option = filterData.data.find(
    (entry) => getFilterOptionValue(entry) === value
  );

  if (option == null) {
    return undefined;
  }

  return getFilterOptionLabel(option);
}

export function resolveComparisonDiscriminatorDisplayValue(
  pageData: PageDto | undefined,
  searchParams: URLSearchParams,
  filterDefinitions?: DataFilterDefinitionsDtoResponse['data']
): string | undefined {
  const { detailsDiscriminator } = getDetailsFromSearchParams(searchParams);
  if (detailsDiscriminator == null) {
    return undefined;
  }

  const pair = parseComparisonDiscriminatorValues(
    searchParams,
    detailsDiscriminator
  );
  if (pair.length !== 2) {
    return undefined;
  }

  const comparisonFilterKey =
    pageData?.pageFilter?.filters?.find(
      (filter) => filter.display === 'comparison'
    )?.key ?? detailsDiscriminator;

  const filterData = filterDefinitions?.[comparisonFilterKey];
  const displayParts = pair.map(
    (entityId) => findOptionLabel(filterData, entityId) ?? entityId
  );

  return displayParts.join(' vs ');
}

export function mergeComparisonNavItemLabels(
  storedNavItemLabels: NavItemLabelDto[] | null | undefined,
  resolvedNavItemLabels: NavItemLabelDto[]
): NavItemLabelDto[] {
  const resolvedDiscriminator = resolvedNavItemLabels.find(
    (label) => label.kind === 'detailsDiscriminatorValue'
  );

  if (
    storedNavItemLabels == null ||
    storedNavItemLabels.length === 0 ||
    resolvedDiscriminator?.displayValue == null
  ) {
    return resolvedNavItemLabels;
  }

  const hasStoredDiscriminator = storedNavItemLabels.some(
    (label) => label.kind === 'detailsDiscriminatorValue'
  );

  if (!hasStoredDiscriminator) {
    return [...storedNavItemLabels, resolvedDiscriminator];
  }

  return storedNavItemLabels.map((label) =>
    label.kind === 'detailsDiscriminatorValue'
      ? { ...label, displayValue: resolvedDiscriminator.displayValue }
      : label
  );
}

export function isPageFilterQueryKeyForInstance(
  queryKey: unknown,
  pageFilterInstanceId: string | null | undefined
): boolean {
  if (pageFilterInstanceId == null || !Array.isArray(queryKey)) {
    return false;
  }

  if (queryKey[1] !== 'pageFilters') {
    return false;
  }

  const meta = queryKey[2];
  return (
    typeof meta === 'object' &&
    meta !== null &&
    'filterKey' in meta &&
    meta.filterKey === pageFilterInstanceId
  );
}

export function findCachedPageFilterDefinitions(
  queryClient: QueryClient,
  pageFilterInstanceId: string | null | undefined
): DataFilterDefinitionsDtoResponse['data'] | undefined {
  if (pageFilterInstanceId == null) {
    return undefined;
  }

  const entries = queryClient.getQueriesData<DataFilterDefinitionsDtoResponse>({
    predicate: (query) => {
      const key = query.queryKey;
      if (!Array.isArray(key) || key[1] !== 'pageFilters') {
        return false;
      }

      const meta = key[2];
      return (
        typeof meta === 'object' &&
        meta !== null &&
        'filterKey' in meta &&
        meta.filterKey === pageFilterInstanceId
      );
    },
  });

  return entries.map(([, data]) => data?.data).find((data) => data != null);
}
